<template>
  <div class="inner">
    <div class="tw:mx-auto tw:my-3 tw:alert tw:w-1/2 tw:alert-error" v-visible="!loadingData && error">
      <AlertCircle />
      <span>{{ error }}</span>
    </div>

    <v-row no-gutters>
      <v-col>
        <h1 data-test="title">Commission Yield Analysis</h1>

        <div class="tw:mt-1 tw:flex tw:justify-between">
          <div class="chart-filters tw:m-2">
            <CoverageTypeFilter
              :coverage-types="allCoverageTypes"
              v-model="selectedCoverageTypes"
              @update:model-value="refreshData" />
            <CarrierGroupFilter
              v-model="selectedCarrierGroups"
              :items="allCarrierGroups"
              @update:model-value="refreshData" />
            <FilterButton
              autofocus
              icon="mdi-format-columns"
              @close="thresholdPercentSubmit"
              :title="thresholdPct"
              :active="thresholdPercent > 0"
              data-test="account-benchmark-view-options"
              data-test-activator="account-benchmark-view-options-activator">
              <template #menu="{ close }">
                <div class="menu">
                  <div class="fields">
                    <div class="field tw:w-40">
                      <v-text-field
                        variant="outlined"
                        density="compact"
                        autofocus
                        clearable
                        class="threshold-box"
                        label="Min. Commission %"
                        @focus="$event.target.select()"
                        v-on:keyup.enter="close"
                        v-model.number="thresholdPercentInput"
                        suffix="%"
                        :rules="[rules.percent]" />
                    </div>
                  </div>
                  <div class="buttons">
                    <v-btn
                      size="small"
                      variant="flat"
                      color="primary"
                      @click="close"
                      data-test="account-benchmark-view-options-apply"
                      >Apply</v-btn
                    >
                  </div>
                </div>
              </template>
            </FilterButton>
          </div>

          <div class="tw:flex tw:items-center tw:justify-end">
            <div class="filters-last-item">
              <ManagedDateRange :initialStartDate="startDate" :initialEndDate="endDate" :onApply="setDate" />
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <div class="tw:flex tw:gap-2">
          <h2>Commission Percentage</h2>
          <download-button @click="downloadTable" />
        </div>
      </v-col>
    </v-row>

    <v-skeleton-loader type="table-tbody" v-if="loadingData && !error" />

    <span class="text-center" v-if="!this.commissions">No data available.</span>

    <v-data-table
      density="compact"
      id="commission-data-table"
      :headers="headers"
      item-key="name"
      class="elevation-1 commission-data"
      :items="commissionData"
      :items-per-page="-1">
      <template v-slot:headers="{ columns }">
        <tr>
          <th style="text-align: center" colspan="1">Carrier</th>
          <th style="text-align: center; width: 10rem" colspan="4">Totals</th>
          <th v-for="coverage in coverageTypes" :key="coverage.text" colspan="4" style="text-align: center">
            {{ coverage }}
          </th>
        </tr>
        <tr>
          <th v-for="col in columns" :key="col.key">{{ col.title }}</th>
        </tr>
      </template>
      <template v-slot:item="{ item }">
        <tr>
          <td class="commission">{{ item.name }}</td>
          <td>{{ item.min }}</td>
          <td>{{ item.max }}</td>
          <td>{{ item.avg }}</td>
          <td class="commission">{{ item.wAvg }}</td>
          <template v-for="coverage in coverageTypes" :key="coverage.text">
            <td>{{ item[`min-val-${coverage}`] }}</td>
            <td>{{ item[`max-val-${coverage}`] }}</td>
            <td>{{ item[`avg-val-${coverage}`] }}</td>
            <td class="commission">{{ item[`wavg-val-${coverage}`] }}</td>
          </template>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import { percentage } from '@/utilities/Number';
import CoverageTypeFilter from '@/components/CoverageTypeFilter.vue';
import CarrierGroupFilter from '@/components/CarrierGroupFilter.vue';
import FilterButton from '@/components/FilterButton.vue';
import ManagedDateRange from '@/components/ManagedDateRange.vue';
import { FailedToLoadPage } from '@/utilities/Errors';
import { carrierService, referenceService } from '@/services/api';
import { DownloadTableToCSV } from '@/utilities/Download';
import { filters } from '@/utilities/Filters';
import { VDataTable } from 'vuetify/components/VDataTable';
import { VSkeletonLoader } from 'vuetify/components/VSkeletonLoader';
import { AlertCircle } from 'lucide-vue-next';
import DownloadButton from '@/components/v2/DownloadButton.vue';

export default {
  name: 'YieldAnalysis',
  components: {
    DownloadButton,
    AlertCircle,
    ManagedDateRange,
    CoverageTypeFilter,
    CarrierGroupFilter,
    FilterButton,
    VDataTable,
    VSkeletonLoader,
  },
  data: () => ({
    list: [],
    commissions: [],
    error: undefined,
    loadingData: true,
    filterText: '',
    selectedCarrierGroups: [],
    selectedCoverageTypes: [],
    allCoverageTypes: [],
    allCarrierGroups: [],
    startDate: DateTime.local().minus({ days: 366 }).toJSDate(),
    endDate: DateTime.local().minus({ days: 1 }).toJSDate(),
    thresholdPercent: 0,
    thresholdPercentInput: 0,
    groupedResults: [],
    rules: {
      percent: (value) => {
        if (!value) {
          return true; // blank input as ok, treat it like 0
        }
        let num = parseFloat(value);
        return isNaN(num) || num < 0 || num > 100 ? 'Value must be 0-100%' : true;
      },
    },
  }),
  methods: {
    async refreshData() {
      if (this.created && this.loadingData) {
        return;
      }
      this.loadingData = true;

      [this.list, this.commissions] = await Promise.all([
        carrierService.list(),
        carrierService.findCommissions(DateTime.fromJSDate(this.startDate), DateTime.fromJSDate(this.endDate)),
      ]);

      if (this.commissions) {
        this.groupedResults = this.commissions.reduce((acc, curr) => {
          if (Number(curr.Commission) < Number(this.thresholdPercent)) {
            return acc;
          }
          acc[curr.InsurerGroup.toLowerCase()] = acc[curr.InsurerGroup.toLowerCase()] || [];
          acc[curr.InsurerGroup.toLowerCase()].push(curr);
          return acc;
        }, {});
      }
      //filter out carriers with no commissions
      this.allCarrierGroups = this.allCarrierGroups.filter(
        (x) => this.groupedResults[x.value.toLowerCase()] && this.groupedResults[x.value.toLowerCase()].length !== 0,
      );

      //filter out coveragetypes with no commissions
      this.allCoverageTypes = this.allCoverageTypes.filter((coverage) =>
        this.commissions.some((x) => x.CoverageType.toLowerCase() === coverage.value.toLowerCase()),
      );

      this.loadingData = false;
    },
    async thresholdPercentSubmit() {
      if (this.thresholdPercentInput === this.thresholdPercent) {
        return;
      }
      this.thresholdPercent = this.thresholdPercentInput;
      await this.refreshData();
    },
    async loadCoverageTypes() {
      this.allCoverageTypes = (await referenceService.coverageTypes())
        .filter((item) => item.label.toLowerCase() !== 'fees')
        .map((ct) => ({ text: ct.label, value: ct.label }));
    },
    async loadCarrierGroups() {
      this.allCarrierGroups = (await referenceService.carrierGroups()).map((ct) => ({
        text: ct.label,
        value: ct.label,
      }));
    },
    async refreshFilters() {
      this.loadingFilters = true;
      await Promise.all([this.loadCoverageTypes(), this.loadCarrierGroups()]);
      this.loadingFilters = false;
    },
    setDate(datePayload) {
      if (datePayload.startDate) {
        this.startDate = datePayload.startDate;
      }
      if (datePayload.endDate) {
        this.endDate = datePayload.endDate;
      }
      this.refreshData();
    },
    getAllForName(name) {
      return this.groupedResults[name.toLowerCase()] || [];
    },
    getAllForType(name, type) {
      return (
        (this.groupedResults[name.toLowerCase()] || []).filter(
          (x) => x.CoverageType.toLowerCase() === type.toLowerCase(),
        ) || []
      );
    },
    findMinForGroup(name) {
      const allCommissions = this.getAllForName(name).filter((x) =>
        this.coverageTypes.find((y) => y === x.CoverageType),
      );
      const result = allCommissions.reduce(
        (acc, curr) => (curr.Commission < Number(acc.Commission) ? curr : acc),
        allCommissions[0],
      );
      return result ? percentage(Number(result.Commission), 1) : '';
    },
    findMaxForGroup(name) {
      const allCommissions = this.getAllForName(name).filter((x) =>
        this.coverageTypes.find((y) => y === x.CoverageType),
      );
      const result = allCommissions.reduce(
        (acc, curr) => (curr.Commission > Number(acc.Commission) ? curr : acc),
        allCommissions[0],
      );
      return result ? percentage(Number(result.Commission), 1) : '';
    },
    findAvgForGroup(name) {
      let allCommissions = this.getAllForName(name).filter((x) => this.coverageTypes.find((y) => y === x.CoverageType));
      const result = allCommissions.reduce((acc, curr) => acc + Number(curr.Commission), 0) / allCommissions.length;
      return isFinite(result) ? percentage(Number(result), 1) : '';
    },
    findSumAvgForGroup(name) {
      const allCommissions = this.getAllForName(name).filter((x) =>
        this.coverageTypes.find((y) => y === x.CoverageType),
      );
      let sumCommsBilled = allCommissions.reduce((acc, curr) => acc + Number(curr.CommissionBilled), 0);
      let sumPolicyPremium = allCommissions.reduce((acc, curr) => acc + Number(curr.PolicyPremium), 0);

      const result = (sumCommsBilled / sumPolicyPremium) * 100;
      return isFinite(result) ? percentage(Number(result), 1) : '';
    },

    findMin(name, type) {
      const allCommissions = this.getAllForType(name, type);
      const result = allCommissions.reduce(
        (acc, curr) => (curr.Commission < Number(acc.Commission) ? curr : acc),
        allCommissions[0],
      );
      return result ? percentage(Number(result.Commission), 1) : '';
    },
    findMax(name, type) {
      const allCommissions = this.getAllForType(name, type);
      const result = allCommissions.reduce(
        (acc, curr) => (curr.Commission > Number(acc.Commission) ? curr : acc),
        allCommissions[0],
      );
      return result ? percentage(Number(result.Commission), 1) : '';
    },
    findAvg(name, type) {
      let allCommissions = this.getAllForType(name, type);
      const result = allCommissions.reduce((acc, curr) => acc + Number(curr.Commission), 0) / allCommissions.length;
      return isFinite(result) ? percentage(Number(result), 1) : '';
    },
    findSumAvg(name, type) {
      let allCommissions = this.getAllForType(name, type);
      let sumCommsBilled = allCommissions.reduce((acc, curr) => acc + Number(curr.CommissionBilled), 0);
      let sumPolicyPremium = allCommissions.reduce((acc, curr) => acc + Number(curr.PolicyPremium), 0);

      const result = (sumCommsBilled / sumPolicyPremium) * 100;
      return isFinite(result) ? percentage(Number(result), 1) : '';
    },
    downloadTable() {
      DownloadTableToCSV('#commission-data-table', 'Commission Percentage', true);
    },
  },
  computed: {
    headers() {
      return [
        {
          key: 'carrier',
          title: '',
          sortable: false,
          align: 'start',
          value: 'name',
          cellClass: 'commission',
          class: 'commission',
          width: '15rem',
        },
        {
          key: 'total-min',
          title: 'Min',
          sortable: false,
          align: 'end',
          value: 'min',
          cellClass: 'bold-total',
          width: '4rem',
        },
        {
          key: 'total-max',
          title: 'Max',
          sortable: false,
          align: 'end',
          value: 'max',
          cellClass: 'bold-total',
          width: '4rem',
        },
        {
          key: 'total-avg',
          title: 'Avg',
          sortable: false,
          align: 'end',
          value: 'avg',
          cellClass: 'bold-total',
          width: '4rem',
        },
        {
          key: 'total-w-avg',
          title: 'W-Avg',
          sortable: false,
          align: 'end',
          value: 'wAvg',
          cellClass: 'bold-total commission',
          class: 'commission',
          width: '4rem',
        },
        ...this.coverageTypes.flatMap((coverage) => [
          {
            key: `${coverage}-min`,
            title: 'Min',
            sortable: false,
            align: 'end',
            value: `min-val-${coverage}`,
            width: '4rem',
          },
          {
            key: `${coverage}-max`,
            title: 'Max',
            sortable: false,
            align: 'end',
            value: `max-val-${coverage}`,
            width: '4rem',
          },
          {
            key: `${coverage}-avg`,
            title: 'Avg',
            sortable: false,
            align: 'end',
            value: `avg-val-${coverage}`,
            width: '4rem',
          },
          {
            key: `${coverage}-w-avg`,
            title: 'W-Avg',
            sortable: false,
            align: 'end',
            class: 'commission',
            cellClass: 'commission',
            value: `wavg-val-${coverage}`,
          },
        ]),
      ];
    },
    commissionData() {
      return this.carrierGroups
        .map((carrier) => {
          const cData = {
            name: carrier,
            min: this.findMinForGroup(carrier) ?? '--',
            max: this.findMaxForGroup(carrier) ?? '--',
            avg: this.findAvgForGroup(carrier) ?? '--',
            wAvg: this.findSumAvgForGroup(carrier) ?? '--',
          };

          this.coverageTypes.forEach((coverage) => {
            cData[`min-val-${coverage}`] = this.findMin(carrier, coverage) ?? '--';
            cData[`max-val-${coverage}`] = this.findMax(carrier, coverage) ?? '--';
            cData[`avg-val-${coverage}`] = this.findAvg(carrier, coverage) ?? '--';
            cData[`wavg-val-${coverage}`] = this.findSumAvg(carrier, coverage) ?? '--';
          });

          return cData;
        })
        .filter((c) => {
          const vals = new Set([c.min, c.max, c.avg, c.wAvg]);
          return !(vals.size === 1 && vals.has('--')); // if all dashempty (--) then filter it out of the result
        });
    },
    coverageTypes() {
      if (this.selectedCoverageTypes.length === 0) {
        return this.allCoverageTypes.map((x) => x.value);
      }
      return this.allCoverageTypes
        .filter((x) => this.selectedCoverageTypes.find((y) => y.toLowerCase() === x.value.toLowerCase()))
        .map((x) => x.value);
    },
    carrierGroups() {
      if (this.selectedCarrierGroups.length > 0) {
        return this.allCarrierGroups
          .filter((x) => this.selectedCarrierGroups.find((y) => y.toLowerCase() === x.value.toLowerCase()))
          .map((x) => x.value);
      }
      return this.allCarrierGroups.map((x) => x.value);
    },
    thresholdPct() {
      return this.thresholdPercent > 0 ? filters.percentage(this.thresholdPercent / 100) : 'Min. Commission';
    },
  },
  created() {
    try {
      void this.refreshFilters();
      void this.refreshData();
      return true;
    } catch (e) {
      console.error(e);
      this.error = FailedToLoadPage;
      return false;
    } finally {
      this.created = true;
      this.loadingData = false;
    }
  },
};
</script>

<style scoped>
.commission-data,
.commission-data table {
  display: block;
  max-width: -moz-fit-content;
  max-width: fit-content;
  margin: 0 0;
  overflow-x: auto;
  white-space: nowrap;
}

.commission-data table > tbody > tr > td:nth-child(1),
.commission-data table > thead > tr > th:nth-child(1) {
  position: sticky !important;
  position: -webkit-sticky !important;
  left: 0;
  background: white;
}

.heading {
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: 1rem;
  align-content: center;
  align-items: center;
}

.chart-filters {
  display: flex;
  justify-items: center;
  grid-template-columns: auto auto auto 1fr;
}

.threshold-box {
  max-width: 10em;
}

.filters-last-item {
  justify-self: end;
}

.menu {
  padding: 1rem 0 0 0;
  min-width: 100px;
}

.menu > h4 {
  padding: 0 1rem;
}

.menu > .fields {
  display: grid;
  padding: 1rem;
}

.menu > .fields > .field > label {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 0.5rem;
  align-items: center;
  align-content: center;
  padding: 0.2rem 0;
  cursor: pointer;
  user-select: none;
}

.menu > .buttons {
  padding: 1rem;
  border-top: 1px solid var(--v-border-base);
  background: var(--v-gray-lighten5);
  display: flex;
  justify-content: flex-end;
}
</style>

<style>
.commission-data table > tbody > tr > td:nth-child(1),
.commission-data table > thead > tr > th:nth-child(1) {
  position: sticky !important;
  position: -webkit-sticky !important;
  left: 0;
  background: white;
}

.bold-total {
  font-weight: bold;
}

th.commission,
td.commission {
  border-right: 1px solid #cbcbcb;
}
</style>
